import { ApiMentionListingApplicationNotification } from 'api/notification/types/ApiMentionListingApplicationNotification';
import { ApiNewListingApplicationNotification } from 'api/notification/types/ApiNewListingApplicationNotification';
import { ApiNewSuggestedApplicantsNotification } from 'api/notification/types/ApiNewSuggestedApplicantsNotification';
import { ApiNotification } from 'api/notification/types/ApiNotification';
import { ApplicantsByListingMappedNotification } from 'types/mappedNotifications/ApplicantsByListingMappedNotification';
import { MappedNotification } from 'types/mappedNotifications/MappedNotification';
import { MentionsByApplicationMappedNotification } from 'types/mappedNotifications/MentionsByApplicationMappedNotification';
import { NewSuggestedApplicantsByJobMappedNotification } from 'types/mappedNotifications/NewSuggestedApplicantsByJobMappedNotification';
import { sortBy } from 'utils/functional/array';

export const getMappedNotificationIds = (notification: MappedNotification) => {
  if (notification.type === 'NEW_APPLICATIONS_BY_LISTING') {
    return notification.data.applications.map(
      (application) => application.notificationId,
    );
  }
  if (notification.type === 'NEW_MENTIONS_BY_APPLICATION') {
    return notification.data.mentions.map((mention) => mention.notificationId);
  }
  if (notification.type === 'NEW_SUGGESTED_APPLICANTS_BY_JOB') {
    return notification.data.batches.map((batch) => batch.notificationId);
  }

  throw Error('Unknown notification type');
};

export function sortNotifications(
  unsortedNotifications: ApiNotification[],
): MappedNotification[] {
  const {
    applicantNotifications,
    mentionNotifications,
    suggestedApplicantsNotifications,
  } = unsortedNotifications.reduce(
    (acc, notification) => {
      if (notification.type === 'NEW_LISTING_APPLICATION') {
        acc.applicantNotifications.push(notification);
      } else if (notification.type === 'MENTION_LISTING_APPLICATION') {
        acc.mentionNotifications.push(notification);
      } else if (notification.type === 'NEW_SUGGESTED_APPLICANTS') {
        acc.suggestedApplicantsNotifications.push(notification);
      }
      return acc;
    },
    {
      applicantNotifications: [] as ApiNewListingApplicationNotification[],
      mentionNotifications: [] as ApiMentionListingApplicationNotification[],
      suggestedApplicantsNotifications:
        [] as ApiNewSuggestedApplicantsNotification[],
    },
  );

  const applicantNotificationsByListingId: Record<
    string,
    ApplicantsByListingMappedNotification
  > = applicantNotifications.reduce(
    (
      acc: Record<string, ApplicantsByListingMappedNotification>,
      notification,
    ) => {
      const notificationListing = notification.data.listingApplication.listing;
      const notificationForListing = acc[notificationListing.id] || {
        type: 'NEW_APPLICATIONS_BY_LISTING',
        data: {
          ...notificationListing,
          applications: [],
          orgId: notification.about.id,
          status: notification.data.listingApplication.status,
          count: 0,
        },
        read: notification.read,
        viewed: notification.viewed,
      };
      notificationForListing.data.count += 1;
      notificationForListing.created =
        acc[notificationListing.id] &&
        new Date(acc[notificationListing.id].created) >
          new Date(notification.created)
          ? acc[notificationListing.id].created
          : notification.created;
      notificationForListing.data.applications.push({
        id: notification.data.listingApplication.id,
        notificationId: notification.id,
      });
      return {
        ...acc,
        [notificationForListing.data.id]: notificationForListing,
      };
    },
    {},
  );

  const mentionNotificationsByApplicationId: Record<
    string,
    MentionsByApplicationMappedNotification
  > = mentionNotifications.reduce(
    (
      acc: Record<string, MentionsByApplicationMappedNotification>,
      notification,
    ) => {
      const notificationApplication = notification.data.listingApplication;

      const notificationForApplication = acc[notificationApplication.id] || {
        type: 'NEW_MENTIONS_BY_APPLICATION',
        data: {
          ...notificationApplication,
          mentions: [],
          orgId: notification.about.id,
          status: notification.data.listingApplication.status,
          count: 0,
        },
        read: notification.read,
        viewed: notification.viewed,
      };
      notificationForApplication.data.count += 1;
      notificationForApplication.created =
        acc[notificationApplication.id] &&
        new Date(acc[notificationApplication.id].created) >
          new Date(notification.created)
          ? acc[notificationApplication.id].created
          : notification.created;
      notificationForApplication.data.mentions.push({
        id: notification.data.listingApplication.id,
        notificationId: notification.id,
      });
      return {
        ...acc,
        [notificationForApplication.data.id]: notificationForApplication,
      };
    },
    {},
  );

  const suggestedApplicantNotificationsByJobId: Record<
    string,
    NewSuggestedApplicantsByJobMappedNotification
  > = suggestedApplicantsNotifications.reduce(
    (
      acc: Record<string, NewSuggestedApplicantsByJobMappedNotification>,
      notification,
    ) => {
      const { job } = notification.data.suggestedApplicantBatch;

      const notificationForJob = acc[job.id] || {
        type: 'NEW_SUGGESTED_APPLICANTS_BY_JOB',
        data: {
          job,
          orgId: notification.about.id,
          count: 0,
          batches: [],
        },
        read: notification.read,
        viewed: notification.viewed,
      };
      notificationForJob.data.count +=
        notification.data.suggestedApplicantBatch.applicantCount;
      notificationForJob.created =
        acc[job.id] &&
        new Date(acc[job.id].created) > new Date(notification.created)
          ? acc[job.id].created
          : notification.created;
      notificationForJob.data.batches.push({
        id: notification.data.suggestedApplicantBatch.id,
        notificationId: notification.id,
      });
      return { ...acc, [notificationForJob.data.job.id]: notificationForJob };
    },
    {},
  );

  const notifications: MappedNotification[] = [
    ...Object.values(applicantNotificationsByListingId),
    ...Object.values(mentionNotificationsByApplicationId),
    ...Object.values(suggestedApplicantNotificationsByJobId),
  ];

  return sortBy(notifications, (n) => new Date(n.created), true);
}
